import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Key,
  Webhook,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Code,
} from 'lucide-react';

export default function APIConfigPage() {
  const { merchant, api, refreshMerchant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(merchant?.webhook_url || '');

  useEffect(() => {
    if (merchant?.webhook_url) {
      setWebhookUrl(merchant.webhook_url);
    }
  }, [merchant]);

  const generateApiKey = async () => {
    if (merchant?.kyc_status !== 'approved') {
      toast.error('KYC must be approved to generate API key');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/merchants/me/generate-api-key');
      await refreshMerchant();
      toast.success('API key generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate API key');
    } finally {
      setLoading(false);
    }
  };

  const saveWebhook = async () => {
    try {
      setLoading(true);
      await api.put('/merchants/me/webhook', { webhook_url: webhookUrl });
      await refreshMerchant();
      toast.success('Webhook URL saved');
    } catch (error) {
      toast.error('Failed to save webhook URL');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const maskedKey = merchant?.api_key 
    ? `${merchant.api_key.slice(0, 12)}${'*'.repeat(20)}${merchant.api_key.slice(-8)}`
    : null;

  const sampleCode = `// Initialize payment
const response = await fetch('${process.env.REACT_APP_BACKEND_URL}/api/transactions/initiate', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    amount: 1000.00,
    currency: 'INR',
    customer_email: 'customer@example.com',
    description: 'Order #12345'
  })
});

const { transaction_id, payment_url } = await response.json();
// Redirect customer to payment_url`;

  if (!merchant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="card-grid max-w-md text-center">
          <CardContent className="pt-6">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-heading font-semibold mb-2">Merchant Profile Required</h3>
            <p className="text-muted-foreground text-sm">
              Complete your merchant onboarding to access API configuration.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold">API Configuration</h1>
        <p className="text-muted-foreground text-sm">Manage your API keys and webhook settings</p>
      </div>

      {/* KYC Status Warning */}
      {merchant.kyc_status !== 'approved' && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="py-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="font-medium">KYC Verification Required</p>
              <p className="text-sm text-muted-foreground">
                API keys can only be generated after KYC approval.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Key Section */}
        <Card className="card-grid">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-heading">API Key</CardTitle>
            </div>
            <CardDescription>
              Use this key to authenticate API requests
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {merchant.api_key ? (
              <>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Live API Key</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        type={showApiKey ? 'text' : 'password'}
                        value={showApiKey ? merchant.api_key : maskedKey}
                        readOnly
                        className="font-mono text-sm rounded-sm pr-20"
                        data-testid="api-key-input"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setShowApiKey(!showApiKey)}
                          data-testid="toggle-key-visibility"
                        >
                          {showApiKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(merchant.api_key)}
                          data-testid="copy-api-key"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={generateApiKey}
                  disabled={loading}
                  className="w-full rounded-sm"
                  data-testid="regenerate-key-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Regenerate Key
                </Button>
                <p className="text-xs text-muted-foreground">
                  Warning: Regenerating will invalidate your current API key.
                </p>
              </>
            ) : (
              <div className="text-center py-6">
                <Key className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground mb-4">No API key generated yet</p>
                <Button
                  onClick={generateApiKey}
                  disabled={loading || merchant.kyc_status !== 'approved'}
                  className="rounded-sm glow-primary"
                  data-testid="generate-key-btn"
                >
                  {loading ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Key className="h-4 w-4 mr-2" /> Generate API Key</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Webhook Configuration */}
        <Card className="card-grid">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Webhook className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-heading">Webhook Configuration</CardTitle>
            </div>
            <CardDescription>
              Receive real-time transaction updates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="webhook-url" className="text-xs uppercase tracking-wider">
                Webhook URL
              </Label>
              <Input
                id="webhook-url"
                type="url"
                placeholder="https://yoursite.com/webhook"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="rounded-sm"
                data-testid="webhook-url-input"
              />
            </div>
            <Button
              onClick={saveWebhook}
              disabled={loading || !webhookUrl}
              className="w-full rounded-sm"
              data-testid="save-webhook-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Webhook URL
            </Button>
            
            <div className="pt-4 border-t border-border/50">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Webhook Events
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="rounded-sm">payment.success</Badge>
                <Badge variant="outline" className="rounded-sm">payment.failed</Badge>
                <Badge variant="outline" className="rounded-sm">payment.refunded</Badge>
                <Badge variant="outline" className="rounded-sm">settlement.processed</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Code Sample */}
      <Card className="card-grid">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Code className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg font-heading">Integration Example</CardTitle>
          </div>
          <CardDescription>Quick start code for payment initiation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <pre className="bg-muted/50 p-4 rounded-sm overflow-x-auto text-sm">
              <code className="font-mono text-foreground">{sampleCode}</code>
            </pre>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8"
              onClick={() => copyToClipboard(sampleCode)}
              data-testid="copy-code-btn"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* API Endpoints */}
      <Card className="card-grid">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Available Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { method: 'POST', path: '/api/transactions/initiate', desc: 'Create new payment' },
              { method: 'GET', path: '/api/transactions/{id}/status', desc: 'Check transaction status' },
              { method: 'POST', path: '/api/transactions/{id}/refund', desc: 'Initiate refund' },
            ].map((endpoint, i) => (
              <div key={i} className="flex items-center gap-4 py-2 border-b border-border/50 last:border-0">
                <Badge 
                  variant="outline" 
                  className={`rounded-sm font-mono ${
                    endpoint.method === 'POST' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-blue-500/10 text-blue-500 border-blue-500/30'
                  }`}
                >
                  {endpoint.method}
                </Badge>
                <code className="font-mono text-sm flex-1">{endpoint.path}</code>
                <span className="text-sm text-muted-foreground">{endpoint.desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
